/**
 * Environment Variables Configuration
 * 
 * Centralized, type-safe access to environment variables with validation.
 * Use this instead of directly accessing import.meta.env throughout the app.
 */

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

/**
 * Validates that a required environment variable is set
 */
function requireEnv(name: string): string {
  const value = import.meta.env[name];
  
  if (!value || value.trim() === '') {
    const errorMessage = `Missing required environment variable: ${name}`;
    
    if (isDevelopment) {
      console.error(`❌ ${errorMessage}`);
      console.error(`\n📝 To fix this:`);
      console.error(`1. Copy .env.example to .env`);
      console.error(`2. Fill in your ${name} value`);
      console.error(`3. Restart your development server\n`);
      
      // In development, throw error to catch issues early
      throw new Error(errorMessage);
    } else {
      // In production, log warning but don't break the app
      console.warn(`⚠️ ${errorMessage}`);
    }
  }
  
  return value || '';
}

/**
 * Gets an optional environment variable with a default value
 */
export function getEnv(name: string, defaultValue: string = ''): string {
  return import.meta.env[name] || defaultValue;
}

/**
 * Validated Supabase URL
 */
export const SUPABASE_URL = requireEnv('VITE_SUPABASE_URL');

/**
 * Validated Supabase Publishable Key (anon/public key)
 */
export const SUPABASE_PUBLISHABLE_KEY = requireEnv('VITE_SUPABASE_PUBLISHABLE_KEY');

/**
 * Feedback form endpoint (optional, has fallback)
 */
export const FEEDBACK_ENDPOINT = getEnv(
  'VITE_FEEDBACK_ENDPOINT',
  'https://formsubmit.co/ajax/cardmadness33@gmail.com'
);

/**
 * Google Analytics 4 Measurement ID (optional)
 */
export const GA4_MEASUREMENT_ID = getEnv('VITE_GA4_MEASUREMENT_ID', '');

/**
 * Sentry DSN for Error Tracking (optional)
 */
export const SENTRY_DSN = getEnv('VITE_SENTRY_DSN', '');

/**
 * Environment mode
 */
export const ENV = {
  MODE: import.meta.env.MODE,
  DEV: isDevelopment,
  PROD: isProduction,
} as const;

/**
 * Validates all required environment variables are set
 * Call this at app startup to catch missing variables early
 */
export function validateEnv(): void {
  try {
    requireEnv('VITE_SUPABASE_URL');
    requireEnv('VITE_SUPABASE_PUBLISHABLE_KEY');
    
    if (isDevelopment) {
      console.log('✅ Environment variables validated');
    }
  } catch (error) {
    // Error already logged in requireEnv
    if (isDevelopment) {
      throw error;
    }
  }
}

